import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Download, ChevronLeft, ChevronRight, X, FileText, Loader2 } from 'lucide-react';
import SEOHead, { seoConfig } from '../components/SEOHead';
import { API_URL } from '../config';



// Image component with loading state
const LazyImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      )}
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <span className="text-gray-500 text-sm">Ошибка загрузки</span>
        </div>
      ) : (
        <img 
          src={src}
          alt={alt}
          className={`w-full h-full object-cover transition-opacity ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          loading="lazy"
        />
      )}
    </div>
  );
};

const InstallationPage = () => {
  const { language } = useLanguage();
  const [selectedSection, setSelectedSection] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeCategory, setActiveCategory] = useState('all');
  const seo = seoConfig.installation[language] || seoConfig.installation.en || seoConfig.installation.ru;

  const content = {
    ru: {
      header: {
        title: 'Монтаж',
        subtitle: 'Альбом технических решений по монтажу гипсовиниловых панелей'
      },
      downloadPdf: 'Скачать PDF альбом',
      sections: 'Разделы',
      slideOf: 'из',
      close: 'Закрыть',
      prev: 'Назад',
      next: 'Далее',
      viewDetails: 'Подробнее',
      categories: {
        all: 'Все разделы',
        scheme: 'Схема монтажа',
        instruction: 'Инструкция',
        joints: 'Стыковочные решения',
        corners: 'Угловые решения',
        frame: 'Монтаж каркаса',
        partitions: 'Монтаж перегородок',
        prep: 'Подготовительные работы',
        extra: 'Доп технические узлы'
      }
    },
    kz: {
      header: {
        title: 'Монтаж',
        subtitle: 'Гипсовинил панельдерін орнату бойынша техникалық шешімдер альбомы'
      },
      downloadPdf: 'PDF альбомын жүктеу',
      sections: 'Бөлімдер',
      slideOf: 'ішінен',
      close: 'Жабу',
      prev: 'Артқа',
      next: 'Алға',
      viewDetails: 'Толығырақ',
      categories: {
        all: 'Барлық бөлімдер',
        scheme: 'Монтаж схемасы',
        instruction: 'Нұсқаулық',
        joints: 'Жалғау шешімдері',
        corners: 'Бұрыш шешімдері',
        frame: 'Каркас монтажы',
        partitions: 'Қалқа монтажы',
        prep: 'Дайындық жұмыстары',
        extra: 'Қосымша техникалық түйіндер'
      }
    },
    en: {
      header: {
        title: 'Installation',
        subtitle: 'Technical solutions album for installing gypsum-vinyl panels'
      },
      downloadPdf: 'Download PDF album',
      sections: 'Sections',
      slideOf: 'of',
      close: 'Close',
      prev: 'Previous',
      next: 'Next',
      viewDetails: 'View details',
      categories: {
        all: 'All sections',
        scheme: 'Mounting Scheme',
        instruction: 'Instruction',
        joints: 'Joint solutions',
        corners: 'Corner solutions',
        frame: 'Frame installation',
        partitions: 'Partition installation',
        prep: 'Preparatory work',
        extra: 'Additional technical nodes'
      }
    },
    ar: {
      header: {
        title: 'التركيب',
        subtitle: 'ألبوم الحلول التقنية لتركيب ألواح الجبس فينيل'
      },
      downloadPdf: 'تحميل ألبوم PDF',
      sections: 'الأقسام',
      slideOf: 'من',
      close: 'إغلاق',
      prev: 'السابق',
      next: 'التالي',
      viewDetails: 'عرض التفاصيل',
      categories: {
        all: 'جميع الأقسام',
        scheme: 'مخطط التركيب',
        instruction: 'التعليمات',
        joints: 'حلول الوصلات',
        corners: 'حلول الزوايا',
        frame: 'تركيب الهيكل',
        partitions: 'تركيب الحواجز',
        prep: 'أعمال التحضير',
        extra: 'العقد التقنية الإضافية'
      }
    }
  };

  // Installation sections with categories
  const installationSections = [
    // Схема монтажа
    {
      id: 0,
      category: 'scheme',
      title: {
        ru: 'Схема монтажа',
        kz: 'Монтаж схемасы',
        en: 'Mounting Scheme', ar: 'مخطط التركيب'
      },
      description: {
        ru: 'Общая схема монтажа панелей GIPSOVINIL с указанием всех типов профилей и этапов установки.',
        kz: 'Барлық профиль түрлері мен орнату кезеңдерін көрсететін GIPSOVINIL панельдерін монтаждау схемасы.',
        en: 'General mounting scheme for GIPSOVINIL panels showing all profile types and installation stages.', ar: 'مخطط التركيب العام لألواح GIPSOVINIL يوضح جميع أنواع الملفات الشخصية ومراحل التثبيت.'
      },
      coverImage: '/images/montage_scheme.webp',
      slides: [
        {
          title: { ru: 'Схема монтажа панелей', kz: 'Панельдерді монтаждау схемасы', en: 'Panel Mounting Scheme', ar: 'مخطط تركيب الألواح' },
          image: '/images/montage_scheme.webp',
          content: {
            ru: 'Схема показывает все основные элементы системы монтажа: стоечный профиль и подвесы, саморезы, F-профиль (наружный, оконный, дверной), Омега-профиль и Т-профиль крышка, L-профиль (внутренний, потолочный, напольный), Омега-профиль и Пи-профиль. Этапы: Стена → Установка каркаса → Монтаж панелей.',
            kz: 'Схема монтаж жүйесінің барлық негізгі элементтерін көрсетеді: тіреу профилі мен аспалар, бұрандалар, F-профиль, Омега-профиль мен Т-профиль қақпақ, L-профиль, Омега-профиль мен Пи-профиль. Кезеңдер: Қабырға → Каркасты орнату → Панельдерді монтаждау.',
            en: 'The scheme shows all main elements of the mounting system: stud profile and suspenders, screws, F-profile (external, window, door), Omega-profile and T-profile cap, L-profile (internal, ceiling, floor), Omega-profile and Pi-profile. Stages: Wall → Frame installation → Panel mounting.', ar: 'يوضح المخطط جميع العناصر الرئيسية لنظام التركيب: البروفيل العمودي (الدعامة) والمعلاق، والمسامير، والبروفيل F (الخارجي، والنافذة، والباب)، وغطاء البروفيل Omega والبروفيل T، والبروفيل L (الداخلي، والسقف، والأرضية)، والبروفيل Omega والبروفيل Pi. المراحل: الجدار ← تركيب الهيكل ← تثبيت الألواح.'
          }
        }
      ]
    },
    // Инструкция по перевозке, хранению, эксплуатации
    {
      id: 100,
      category: 'instruction',
      title: {
        ru: 'Условия перевозки',
        kz: 'Тасымалдау шарттары',
        en: 'Transportation Conditions', ar: 'شروط النقل'
      },
      description: {
        ru: 'Рекомендации по загрузке, выгрузке и транспортировке панелей. Требования к транспорту и способы защиты груза.',
        kz: 'Панельдерді тиеу, түсіру және тасымалдау бойынша ұсыныстар. Көлікке қойылатын талаптар мен жүкті қорғау тәсілдері.',
        en: 'Recommendations for loading, unloading and transporting panels. Transport requirements and cargo protection methods.', ar: 'توصيات بشأن تحميل الألواح وتفريغها ونقلها. متطلبات النقل وأساليب حماية البضائع.'
      },
      coverImage: `${API_URL}/api/instruction/2`,
      slides: [
        {
          title: { ru: 'Загрузка и транспорт', kz: 'Тиеу және көлік', en: 'Loading and Transport', ar: 'التحميل والنقل' },
          image: `${API_URL}/api/instruction/2`,
          content: {
            ru: 'Загрузка производится через боковую часть кузова, предпочтительно в тентованный транспорт. Используйте вилочный погрузчик. Панели перевозятся в паллетированном виде.',
            kz: 'Тиеу шанақтың бүйір жағынан, тентті көлікке жасалады. Шанышқылы тиегішті пайдаланыңыз. Панельдер паллетте тасымалданады.',
            en: 'Loading is done through the side of the body, preferably in a tented transport. Use a forklift. Panels are transported in palletized form.', ar: 'يتم التحميل من الجانب الجانبي للمركبة، ويُفضَّل استخدام مركبات نقل مغطاة. استخدم الرافعة الشوكية. يتم نقل الألواح بشكل ملبّد (بالطبلية).'
          }
        },
        {
          title: { ru: 'Разгрузка панелей', kz: 'Панельдерді түсіру', en: 'Unloading Panels', ar: 'تفريغ الألواح' },
          image: `${API_URL}/api/instruction/3`,
          content: {
            ru: 'Рекомендуется разгружать вилочным погрузчиком. Избегайте ударов по торцам панелей. Краном разгружать не рекомендуется — стропы могут повредить панели.',
            kz: 'Шанышқылы тиегішпен түсіру ұсынылады. Панельдердің ұштарына соққыдан сақтаныңыз. Кранмен түсіру ұсынылмайды — белдіктер панельдерді зақымдауы мүмкін.',
            en: 'Unload with forklift is recommended. Avoid impacts on panel edges. Crane unloading is not recommended — straps may damage panels.', ar: 'يُنصح بالتفريغ باستخدام الرافعة الشوكية. تجنب الصدمات على حواف الألواح. لا يُنصح بالتفريغ بالرافعة العلوية — إذ قد تتسبب الأحزمة في إتلاف الألواح.'
          }
        },
        {
          title: { ru: 'Ручная разгрузка', kz: 'Қолмен түсіру', en: 'Manual Unloading', ar: 'التفريغ اليدوي' },
          image: `${API_URL}/api/instruction/4`,
          content: {
            ru: 'При ручной разгрузке панели поднимают вдвоём. Укладка производится лицом к лицу. Категорически запрещается волочить панели!',
            kz: 'Қолмен түсіру кезінде панельдерді екеуі көтереді. Қабаттау бет-бетке жасалады. Панельдерді сүйреуге қатаң тыйым салынады!',
            en: 'During manual unloading, panels are lifted by two people. Stacking is done face to face. Dragging panels is strictly prohibited!', ar: 'عند التفريغ اليدوي، يُرفع كل لوح بواسطة شخصين. يُرصّ اللوحان وجهاً لوجه. يُحظر سحب الألواح حظراً قاطعاً!'
          }
        }
      ]
    },
    {
      id: 101,
      category: 'instruction',
      title: {
        ru: 'Условия хранения',
        kz: 'Сақтау шарттары',
        en: 'Storage Conditions', ar: 'شروط التخزين'
      },
      description: {
        ru: 'Правила хранения панелей на складе: температура, влажность, защита от солнца и механических повреждений.',
        kz: 'Қоймада панельдерді сақтау ережелері: температура, ылғалдылық, күн сәулесі мен механикалық зақымданудан қорғау.',
        en: 'Rules for storing panels in warehouse: temperature, humidity, protection from sun and mechanical damage.', ar: 'قواعد تخزين الألواح في المستودع: درجة الحرارة، الرطوبة، الحماية من أشعة الشمس والأضرار الميكانيكية.'
      },
      coverImage: `${API_URL}/api/instruction/5`,
      slides: [
        {
          title: { ru: 'Складское хранение', kz: 'Қоймалық сақтау', en: 'Warehouse Storage', ar: 'التخزين في المستودع' },
          image: `${API_URL}/api/instruction/4`,
          content: {
            ru: 'Панели хранятся в крытых проветриваемых складах, защищённых от влаги и прямых солнечных лучей. Температура +5...+35°C, влажность 35-65%.',
            kz: 'Панельдер ылғал мен тікелей күн сәулесінен қорғалған жабық желдетілетін қоймаларда сақталады. Температура +5...+35°C, ылғалдылық 35-65%.',
            en: 'Panels are stored in covered, ventilated warehouses, protected from moisture and direct sunlight. Temperature +5...+35°C, humidity 35-65%.', ar: 'تُخزَّن الألواح في مستودعات مغطّاة وجيّدة التهوية، محمية من الرطوبة وأشعة الشمس المباشرة. درجة الحرارة من +5 إلى +35°C، الرطوبة من 35 إلى 65%.'
          }
        },
        {
          title: { ru: 'Правила хранения', kz: 'Сақтау ережелері', en: 'Storage Rules', ar: 'قواعد التخزين' },
          image: `${API_URL}/api/instruction/5`,
          content: {
            ru: 'Панели хранятся горизонтально на паллетах. Вертикальное хранение запрещено — панели могут деформироваться! Не храните в полиэтиленовой упаковке при перепадах температур.',
            kz: 'Панельдер паллеттерде көлденеңінен сақталады. Тік сақтауға тыйым салынады — панельдер деформациялануы мүмкін! Температура өзгерістері кезінде полиэтилен орамада сақтамаңыз.',
            en: 'Panels are stored horizontally on pallets. Vertical storage is prohibited — panels may deform! Do not store in polyethylene packaging during temperature fluctuations.', ar: 'تُخزَّن الألواح أفقياً على المنصّات. يُحظر التخزين الرأسي — قد تتشوّه الألواح! لا تُخزَّن في تغليف بولي إيثيلين أثناء تقلّبات درجات الحرارة.'
          }
        }
      ]
    },
    {
      id: 102,
      category: 'instruction',
      title: {
        ru: 'Условия монтажа',
        kz: 'Монтаж шарттары',
        en: 'Installation Conditions'
      },
      description: {
        ru: 'Требования к помещению и температурному режиму при монтаже панелей.',
        kz: 'Панельдерді орнату кезінде бөлме мен температуралық режимге қойылатын талаптар.',
        en: 'Requirements for premises and temperature conditions during panel installation.', ar: 'متطلبات المباني وظروف درجة الحرارة أثناء تركيب الألواح.'
      },
      coverImage: `${API_URL}/api/instruction/6`,
      slides: [
        {
          title: { ru: 'Подготовка помещения', kz: 'Бөлмені дайындау', en: 'Room Preparation', ar: 'تحضير الغرفة' },
          image: `${API_URL}/api/instruction/6`,
          content: {
            ru: 'Помещение должно быть сухим, мокрые работы завершены минимум за месяц. Панели должны адаптироваться к температуре и влажности помещения.',
            kz: 'Бөлме құрғақ болуы керек, дымқыл жұмыстар кем дегенде бір ай бұрын аяқталуы керек. Панельдер бөлменің температурасы мен ылғалдылығына бейімделуі керек.',
            en: 'Room must be dry, wet works completed at least a month prior. Panels must adapt to room temperature and humidity.', ar: 'يجب أن تكون الغرفة جافة، وأن تكون أعمال البناء الرطبة قد اكتملت قبل شهر على الأقل. يجب أن تتأقلم الألواح مع درجة حرارة الغرفة ورطوبتها.'
          }
        },
        {
          title: { ru: 'Температурный режим', kz: 'Температуралық режим', en: 'Temperature Conditions', ar: 'الظروف الحرارية' },
          image: `${API_URL}/api/instruction/7`,
          content: {
            ru: 'Рекомендуемая температура монтажа: 18-20°C, влажность: около 50%. Не используйте тепловые пушки направленно на панели. Поддерживайте стабильный режим до и после монтажа.',
            kz: 'Ұсынылатын монтаж температурасы: 18-20°C, ылғалдылық: шамамен 50%. Жылу пушкаларын панельдерге бағыттап пайдаланбаңыз. Монтажға дейін және одан кейін тұрақты режимді сақтаңыз.',
            en: 'Recommended installation temperature: 18-20°C, humidity: about 50%. Do not use heat guns directly on panels. Maintain stable conditions before and after installation.', ar: 'درجة الحرارة الموصى بها للتركيب: 18-20°C، الرطوبة: حوالي 50%. لا تستخدم مسدسات الحرارة مباشرةً على الألواح. حافظ على ظروف مستقرة قبل التركيب وبعده.'
          }
        }
      ]
    },
    {
      id: 103,
      category: 'instruction',
      title: {
        ru: 'Условия эксплуатации',
        kz: 'Пайдалану шарттары',
        en: 'Operation Conditions', ar: 'شروط التشغيل'
      },
      description: {
        ru: 'Правила эксплуатации панелей: температура, влажность, уход и запрещённые действия.',
        kz: 'Панельдерді пайдалану ережелері: температура, ылғалдылық, күтім және тыйым салынған әрекеттер.',
        en: 'Panel operation rules: temperature, humidity, care and prohibited actions.', ar: 'قواعد تشغيل الألواح: درجة الحرارة، والرطوبة، والعناية، والإجراءات المحظورة.'
      },
      coverImage: `${API_URL}/api/instruction/8`,
      slides: [
        {
          title: { ru: 'Уход и эксплуатация', kz: 'Күтім және пайдалану', en: 'Care and Operation', ar: 'العناية والتشغيل' },
          image: `${API_URL}/api/instruction/8`,
          content: {
            ru: 'Температура эксплуатации: +5...+35°C, влажность: 35-65%. Очистка: вода и мягкая ткань или нейтральные моющие средства. Запрещены: резкие перепады температур, высокая влажность, механические повреждения.',
            kz: 'Пайдалану температурасы: +5...+35°C, ылғалдылық: 35-65%. Тазалау: су және жұмсақ мата немесе бейтарап жуғыш заттар. Тыйым салынады: температураның кенет өзгеруі, жоғары ылғалдылық, механикалық зақым.',
            en: 'Operating temperature: +5...+35°C, humidity: 35-65%. Cleaning: water and soft cloth or neutral cleaning agents. Prohibited: sudden temperature changes, high humidity, mechanical damage.', ar: 'درجة حرارة التشغيل: +5...+35°C، الرطوبة: 35-65%. التنظيف: بالماء وقطعة قماش ناعمة أو مواد تنظيف محايدة. محظور: التغيرات المفاجئة في درجات الحرارة، والرطوبة العالية، والأضرار الميكانيكية.'
          }
        }
      ]
    },
    // Подготовительные работы
    {
      id: 1,
      category: 'prep',
      title: {
        ru: 'Общее описание',
        kz: 'Жалпы сипаттама',
        en: 'General Description', ar: 'الوصف العام'
      },
      description: {
        ru: 'Гипсовиниловые панели – это современный материал для внутренней отделки стен. Подсистема монтируется аналогично технологии Knauf.',
        kz: 'Гипсовинил панельдері – ішкі қабырғаларды әрлеуге арналған заманауи материал. Ішкі жүйе Knauf технологиясына ұқсас орнатылады.',
        en: 'Gypsum-vinyl panels are modern material for interior wall decoration. The sub-frame is mounted similar to Knauf technology.', ar: 'ألواح الجبس الفينيلية مادة حديثة لتزيين الجدران الداخلية. يُركَّب الهيكل الفرعي وفق تقنية Knauf.'
      },
      coverImage: `${API_URL}/api/installation/general-1`,
      slides: [
        {
          title: { ru: 'Что такое Гипсовинил', kz: 'Гипсовинил дегеніміз не', en: 'What is GIPSOVINIL.KZ', ar: 'ما هو GIPSOVINIL.KZ' },
          image: `${API_URL}/api/installation/general-1`,
          content: {
            ru: 'Гипсовиниловые панели – это современный материал для внутренней отделки стен. Подсистема для монтажа панелей монтируется аналогично технологии Knauf с учётом применения Омега-профиля.',
            kz: 'Гипсовинил панельдері – ішкі қабырғаларды әрлеуге арналған заманауи материал. Панельдерді орнатуға арналған ішкі жүйе Omega-профилін ескере отырып Knauf технологиясына ұқсас орнатылады.',
            en: 'Gypsum-vinyl panels are modern material for interior wall decoration. The sub-frame for panel installation is mounted similar to Knauf technology, considering the Omega-profile.', ar: 'ألواح الجبس الفينيلية مادة حديثة لتزيين الجدران الداخلية. يُركَّب الهيكل الفرعي لتثبيت الألواح وفق تقنية Knauf، مع مراعاة استخدام الملف أوميغا (Omega-profile).'
          }
        },
        {
          title: { ru: 'Основание панелей', kz: 'Панельдердің негізі', en: 'Panel Base', ar: 'قاعدة اللوح' },
          image: `${API_URL}/api/installation/general-2`,
          content: {
            ru: 'Основанием панелей может быть: гипсокартонный лист, ДСП, цементно-магнезиальный лист. Панели могут устанавливаться в один или два слоя.',
            kz: 'Панельдердің негізі болуы мүмкін: гипсокартон парағы, ДСП, цемент-магний парағы. Панельдер бір немесе екі қабатпен орнатылуы мүмкін.',
            en: 'Panel base can be: gypsum board, chipboard, cement-magnesium sheet. Panels can be installed in one or two layers.', ar: 'يمكن أن تكون القاعدة المحورية من: ألواح الجبس المغلفة (GKL)، أو ألواح الحبيبات الخشبية، أو ألواح الأسمنت والمغنيسيوم (SML). ويمكن تركيب الألواح في طبقة واحدة أو طبقتين.'
          }
        }
      ]
    },
    {
      id: 2,
      category: 'prep',
      title: {
        ru: 'Подготовительные работы',
        kz: 'Дайындық жұмыстары',
        en: 'Preparatory Work', ar: 'أعمال التحضير'
      },
      description: {
        ru: 'Условия для начала монтажа: завершение мокрых работ, полное высыхание поверхностей, оптимальная температура 18-20°C.',
        kz: 'Монтажды бастау шарттары: дымқыл жұмыстардың аяқталуы, беттердің толық құрғауы, оңтайлы температура 18-20°C.',
        en: 'Conditions for starting installation: completion of wet works, complete drying of surfaces, optimal temperature 18-20°C.', ar: 'شروط البدء في التركيب: الانتهاء من الأعمال الرطبة، والجفاف التام للأسطح، ودرجة الحرارة المثلى 18-20°C.'
      },
      coverImage: `${API_URL}/api/installation/prep-1`,
      slides: [
        {
          title: { ru: 'Условия монтажа', kz: 'Монтаж шарттары', en: 'Installation Conditions', ar: 'شروط التركيب' },
          image: `${API_URL}/api/installation/prep-1`,
          content: {
            ru: 'Перед началом монтажа необходимо завершить все мокрые работы, обеспечить полное высыхание потолков, стен и полов. Оптимальная температура: 18-20°C, влажность: около 50%.',
            kz: 'Монтажды бастамас бұрын барлық дымқыл жұмыстарды аяқтау, төбелер, қабырғалар мен еденнің толық құрғауын қамтамасыз ету қажет. Оңтайлы температура: 18-20°C, ылғалдылық: шамамен 50%.',
            en: 'Before starting installation, complete all wet works, ensure complete drying of ceilings, walls, and floors. Optimal temperature: 18-20°C, humidity: approximately 50%.', ar: 'قبل البدء في التركيب، أكمل جميع الأعمال الرطبة، وتأكد من الجفاف التام للأسقف والجدران والأرضيات. درجة الحرارة المثلى: 18-20°C، الرطوبة: حوالي 50%.'
          }
        },
        {
          title: { ru: 'Необходимый инструмент', kz: 'Қажетті құрал-жабдықтар', en: 'Required Tools', ar: 'الأدوات المطلوبة' },
          image: `${API_URL}/api/installation/tools`,
          content: {
            ru: 'Инструменты: дрель с коронкой для отверстий, специальные ножи и режущие станки для панелей, ножницы по металлу для профилей, просечка для соединения профилей.',
            kz: 'Құралдар: тесіктерге арналған тәжі бар бұрғы, панельдерге арналған арнайы пышақтар мен кесу станоктары, профильдерге арналған металл қайшылары, профильдерді қосуға арналған тесу құралы.',
            en: 'Tools: drill with crown for holes, special knives and cutting machines for panels, metal shears for profiles, sheet metal joiner for connecting profiles.', ar: 'الأدوات: مثقاب بتاج لحفر الفتحات، سكاكين خاصة وآلات قطع للألواح، مقص معدني للبروفيلات، وصلة صفائح معدنية لتوصيل البروفيلات.'
          }
        }
      ]
    },
    // Стыковочные решения
    {
      id: 3,
      category: 'joints',
      title: {
        ru: 'Монтаж на клей-пену',
        kz: 'Желім-көбікке орнату',
        en: 'Adhesive-Foam Installation', ar: 'التركيب بالرغوة اللاصقة'
      },
      description: {
        ru: 'Метод прямой установки панелей на ровную стену с использованием клей-пены и жидких гвоздей.',
        kz: 'Желім-көбік пен сұйық шегелерді пайдаланып тегіс қабырғаға панельдерді тікелей орнату әдісі.',
        en: 'Direct installation method on flat wall using adhesive foam and liquid nails.', ar: 'طريقة التركيب المباشر على الجدار المستوي باستخدام رغوة لاصقة ومسامير سائلة.'
      },
      coverImage: `${API_URL}/api/album3/7`,
      slides: [
        {
          title: { ru: 'Схема монтажа на клей', kz: 'Желімге орнату схемасы', en: 'Adhesive Installation Diagram', ar: 'مخطط التركيب اللاصق' },
          image: `${API_URL}/api/album3/6`,
          content: {
            ru: 'Перед установкой необходимо очистить поверхность от пыли и загрунтовать. Метод допускается если отклонения стены соответствуют нормам для прямого монтажа.',
            kz: 'Орнату алдында бетті шаңнан тазартып, грунттау қажет. Қабырғаның ауытқуы тікелей монтаж нормаларына сәйкес келсе, әдіске рұқсат етіледі.',
            en: 'Before installation, clean the surface from dust and prime it. Method is permitted if wall deviations meet direct mounting standards.', ar: 'قبل التركيب، نظِّف السطح من الغبار وطبِّق عليه مادة التأسيس. يُسمح بهذه الطريقة إذا كانت انحرافات الجدار تستوفي معايير التركيب المباشر.'
          }
        },
        {
          title: { ru: 'Процесс монтажа', kz: 'Монтаж процесі', en: 'Installation Process' },
          image: `${API_URL}/api/album3/7`,
          content: {
            ru: 'Клей-пена наносится вертикальными полосами или точечно-линейно. Панель устанавливается с лёгким прижатием для выравнивания. Стыки выполняются с помощью Т-профиля.',
            kz: 'Желім-көбік тік жолақтармен немесе нүктелік-сызықтық түрде жағылады. Панель теңестіру үшін жеңіл басу арқылы орнатылады. Жіктер Т-профиль көмегімен жасалады.',
            en: 'Adhesive foam is applied in vertical strips or point-linear manner. Panel is installed with slight pressure for alignment. Joints are made using T-profile.', ar: 'يُطبَّق الرغوة اللاصقة في شرائط رأسية أو بطريقة نقطية خطية. تُركَّب اللوحة بضغط خفيف لضمان المحاذاة. تُنفَّذ الوصلات باستخدام مقطع T.'
          }
        }
      ]
    },
    {
      id: 4,
      category: 'joints',
      title: {
        ru: 'Монтаж с Т-профилем',
        kz: 'Т-профильмен орнату',
        en: 'T-Profile Installation', ar: 'تركيب مقطع T'
      },
      description: {
        ru: 'Установка панелей на предварительно смонтированный металлический каркас с использованием Т-профиля.',
        kz: 'Алдын ала орнатылған металл каркасқа Т-профилін пайдаланып панельдерді орнату.',
        en: 'Installation of panels on pre-assembled metal frame using T-profile.', ar: 'تركيب اللوحات على هيكل معدني مُجمَّع مسبقاً باستخدام مقطع T.'
      },
      coverImage: `${API_URL}/api/album3/9`,
      slides: [
        {
          title: { ru: 'Схема с Т-профилем', kz: 'Т-профильмен схема', en: 'T-Profile Diagram', ar: 'مخطط المقطع على شكل T' },
          image: `${API_URL}/api/album3/8`,
          content: {
            ru: 'Перед монтажом панелей необходимо проверить прочность, жёсткость и ровность каркаса. Конструкция каркаса описана в Альбоме технических решений №1.',
            kz: 'Панельдерді орнату алдында каркастың беріктігін, қаттылығын және тегістігін тексеру қажет. Каркас құрылымы №1 Техникалық шешімдер альбомында сипатталған.',
            en: 'Before panel installation, check the frame strength, rigidity, and flatness. Frame construction is described in Technical Solutions Album №1.', ar: 'قبل تركيب اللوحات، تحقق من متانة الهيكل وصلابته واستوائه. يُوصف تشييد الهيكل في ألبوم الحلول التقنية رقم 1.'
          }
        },
        {
          title: { ru: 'Крепление панелей', kz: 'Панельдерді бекіту', en: 'Panel Fastening', ar: 'تثبيت الألواح' },
          image: `${API_URL}/api/album3/9`,
          content: {
            ru: 'Панели крепятся к каркасу с помощью жидких гвоздей, наносимых на поверхность каркаса. Стыки панелей выполняются с помощью Т-образного соединительного профиля.',
            kz: 'Панельдер каркас бетіне жағылатын сұйық шегелер көмегімен каркасқа бекітіледі. Панельдердің жіктері Т-тәрізді қосу профилі арқылы жасалады.',
            en: 'Panels are attached to the frame using liquid nails applied to the frame surface. Panel joints are made using T-shaped joining profile.', ar: 'تُثبَّت الألواح في الهيكل باستخدام مسامير سائلة تُطبَّق على سطح الهيكل. تُنفَّذ وصلات الألواح بواسطة قاطعة ربط على شكل حرف T.'
          }
        }
      ]
    },
    {
      id: 5,
      category: 'joints',
      title: {
        ru: 'Монтаж с Н-профилем',
        kz: 'Н-профильмен орнату',
        en: 'H-Profile Installation', ar: 'تركيب قاطعة H'
      },
      description: {
        ru: 'Метод создания бесшовного технологичного стыка между панелями на металлическом каркасе.',
        kz: 'Металл каркаста панельдер арасында тігіссіз технологиялық жік жасау әдісі.',
        en: 'Method for creating seamless technological joint between panels on metal frame.', ar: 'طريقة إنشاء وصلة تقنية سلسة بين الألواح على الهيكل المعدني.'
      },
      coverImage: `${API_URL}/api/album3/11`,
      slides: [
        {
          title: { ru: 'Схема с Н-профилем', kz: 'Н-профильмен схема', en: 'H-Profile Diagram', ar: 'مخطط قاطعة H' },
          image: `${API_URL}/api/album3/10`,
          content: {
            ru: 'Н-профиль используется для соединения панелей. Создаёт бесшовный технологичный стык. Профиль крепится саморезами к каркасу.',
            kz: 'Н-профиль панельдерді қосу үшін қолданылады. Тігіссіз технологиялық жік жасайды. Профиль каркасқа бұрандалармен бекітіледі.',
            en: 'H-profile is used for joining panels. Creates seamless technological joint. Profile is screwed to the frame.', ar: 'تُستخدم قاطعة H لربط الألواح، مما يُنشئ وصلة تقنية سلسة. تُلوَّلب القاطعة في الهيكل.'
          }
        },
        {
          title: { ru: 'Процесс установки', kz: 'Орнату процесі', en: 'Installation Process', ar: 'عملية التركيب' },
          image: `${API_URL}/api/album3/11`,
          content: {
            ru: 'Сначала формируется внутренний угол с L-профилем, затем крепится панель, после чего Н-профиль насаживается на панель и прикручивается к каркасу.',
            kz: 'Алдымен L-профильмен ішкі бұрыш қалыптасады, содан кейін панель бекітіледі, содан кейін Н-профиль панельге кигізіліп, каркасқа бұралады.',
            en: 'First, internal corner is formed with L-profile, then panel is attached, after which H-profile is fitted onto panel and screwed to frame.', ar: 'أولاً، يتم تشكيل الزاوية الداخلية بواسطة قاطعة L، ثم يُثبَّت اللوح، وبعد ذلك تُركَّب قاطعة H على اللوح وتُلوَّلب في الهيكل.'
          }
        }
      ]
    },
    {
      id: 6,
      category: 'joints',
      title: {
        ru: 'Монтаж с Н-теневым профилем',
        kz: 'Н-көлеңке профильмен орнату',
        en: 'H-Shadow Profile Installation', ar: 'تركيب ملف الظل H'
      },
      description: {
        ru: 'Декоративный профиль для создания эффекта "парящей стены" с теневым зазором.',
        kz: 'Көлеңке саңылауы бар "қалқымалы қабырға" әсерін жасауға арналған сәндік профиль.',
        en: 'Decorative profile for creating "floating wall" effect with shadow gap.', ar: 'ملف زخرفي لإيجاد تأثير "الجدار العائم" مع فجوة ظليّة.'
      },
      coverImage: `${API_URL}/api/album3/13`,
      slides: [
        {
          title: { ru: 'Схема Н-теневого профиля', kz: 'Н-көлеңке профиль схемасы', en: 'H-Shadow Profile Diagram', ar: 'مخطط ملف الظل H' },
          image: `${API_URL}/api/album3/12`,
          content: {
            ru: 'Н-теневой профиль создаёт декоративный теневой зазор между панелями, обеспечивая выразительную архитектурную линию и современный эффект "парящей стены".',
            kz: 'Н-көлеңке профилі панельдер арасында сәндік көлеңке саңылауын жасайды, айқын архитектуралық сызық пен заманауи "қалқымалы қабырға" әсерін қамтамасыз етеді.',
            en: 'H-shadow profile creates decorative shadow gap between panels, providing expressive architectural line and modern "floating wall" effect.', ar: 'يُشكّل ملف الظل H فجوةً ظليّة زخرفية بين الألواح، مما يمنح خطاً معمارياً معبّراً وتأثيراً عصرياً لـ"الجدار العائم".'
          }
        },
        {
          title: { ru: 'Область применения', kz: 'Қолдану аймағы', en: 'Application Area', ar: 'مجال التطبيق' },
          image: `${API_URL}/api/album3/13`,
          content: {
            ru: 'Рекомендуется для жилых комплексов, офисов, бизнес-центров и коммерческих помещений, где требуется качество и выразительный дизайн.',
            kz: 'Сапа мен айқын дизайн талап етілетін тұрғын кешендер, кеңселер, бизнес-орталықтар мен коммерциялық үй-жайлар үшін ұсынылады.',
            en: 'Recommended for residential complexes, offices, business centers, and commercial premises where quality and expressive design are required.', ar: 'موصى به للمجمعات السكنية والمكاتب ومراكز الأعمال والمحلات التجارية التي تتطلب جودة عالية وتصميماً معبّراً.'
          }
        }
      ]
    },
    {
      id: 7,
      category: 'joints',
      title: {
        ru: 'Профиль крышка + Омега',
        kz: 'Қақпақ профилі + Омега',
        en: 'Cap + Omega Profile', ar: 'غطاء + ملف أوميغا'
      },
      description: {
        ru: 'Герметичное соединение для медицинских, детских и образовательных учреждений.',
        kz: 'Медициналық, балалар және білім беру мекемелеріне арналған герметикалық қосылыс.',
        en: 'Hermetic connection for medical, childcare, and educational facilities.', ar: 'وصلة محكمة الإغلاق للمنشآت الطبية ودور رعاية الأطفال والمرافق التعليمية.'
      },
      coverImage: `${API_URL}/api/album3/15`,
      slides: [
        {
          title: { ru: 'Схема Омега-профиля', kz: 'Омега-профиль схемасы', en: 'Omega Profile Diagram', ar: 'مخطط بروفيل أوميغا' },
          image: `${API_URL}/api/album3/14`,
          content: {
            ru: 'Панели крепятся к стене с помощью Омега-профиля, который прикручивается к стоечному профилю с шагом 400-600мм. "Крылья" профиля прижимают панели к каркасу.',
            kz: 'Панельдер қабырғаға Омега-профиль көмегімен бекітіледі, ол 400-600мм қадаммен тіреу профиліне бұралады. Профильдің "қанаттары" панельдерді каркасқа қысады.',
            en: 'Panels are attached to wall using Omega profile, which is screwed to stud profile at 400-600mm intervals. Profile "wings" press panels against frame.', ar: 'تُثبَّت الألواح على الجدار باستخدام بروفيل أوميغا، الذي يُلوَّلب على بروفيل الدعامة بفواصل تتراوح بين 400-600 مم. تضغط «أجنحة» البروفيل على الألواح باتجاه الإطار.'
          }
        },
        {
          title: { ru: 'Декоративная крышка', kz: 'Сәндік қақпақ', en: 'Decorative Cap', ar: 'غطاء زخرفي' },
          image: `${API_URL}/api/album3/15`,
          content: {
            ru: 'Крышка устанавливается в Омега-профиль с лицевой стороны. Герметично закрывает профиль, не оставляя щелей. Рекомендуется для медицинских и детских учреждений.',
            kz: 'Қақпақ Омега-профильге алдыңғы жағынан орнатылады. Профильді герметикалық түрде жабады, саңылаулар қалдырмайды. Медициналық және балалар мекемелеріне ұсынылады.',
            en: 'Cap is installed into Omega profile from front side. Hermetically seals profile, leaving no gaps. Recommended for medical and childcare facilities.', ar: 'يُركَّب الغطاء في بروفيل أوميغا من الجهة الأمامية. يُحكم إغلاق البروفيل بشكل محكم دون ترك أي فراغات. يُوصى به للمنشآت الطبية ودور رعاية الأطفال.'
          }
        },
        {
          title: { ru: 'Съёмные панели', kz: 'Алынбалы панельдер', en: 'Removable Panels', ar: 'ألواح قابلة للإزالة' },
          image: `${API_URL}/api/album3/15`,
          content: {
            ru: 'Данная система позволяет делать панели съёмными. Крышку можно аккуратно снять, затем открутить Омега-профиль для доступа к коммуникациям или замены панели.',
            kz: 'Бұл жүйе панельдерді алынбалы етуге мүмкіндік береді. Қақпақты мұқият алып тастауға болады, содан кейін коммуникацияларға қол жеткізу немесе панельді ауыстыру үшін Омега-профильді бұрап алуға болады.',
            en: 'This system allows panels to be removable. Cap can be carefully detached, then Omega profile unscrewed for access to utilities or panel replacement.', ar: 'يتيح هذا النظام إمكانية فك الألواح وإزالتها. يمكن فصل الغطاء بعناية، ثم فك بروفيل أوميغا بالمسامير للوصول إلى التمديدات أو استبدال الألواح.'
          }
        }
      ]
    },
    {
      id: 8,
      category: 'joints',
      title: {
        ru: 'Пи-профиль + Омега',
        kz: 'Пи-профиль + Омега',
        en: 'Pi + Omega Profile', ar: 'قسم Pi + Omega'
      },
      description: {
        ru: 'Бюджетный вариант соединения панелей с использованием Пи-профиля.',
        kz: 'Пи-профилін пайдаланып панельдерді қосудың бюджеттік нұсқасы.',
        en: 'Budget-friendly option for panel connection using Pi-profile.', ar: 'خيار اقتصادي لتوصيل الألواح باستخدام القسم Pi.'
      },
      coverImage: `${API_URL}/api/album3/17`,
      slides: [
        {
          title: { ru: 'Схема Пи + Омега', kz: 'Пи + Омега схемасы', en: 'Pi + Omega Diagram', ar: 'مخطط Pi + Omega' },
          image: `${API_URL}/api/album3/16`,
          content: {
            ru: 'Комбинация Пи-профиля и Омега-профиля – более бюджетный вариант крепления панелей. Омега-профиль заказывается в цвет Пи-профиля по палитре RAL.',
            kz: 'Пи-профиль мен Омега-профильдің комбинациясы – панельдерді бекітудің бюджеттік нұсқасы. Омега-профиль RAL палитрасы бойынша Пи-профиль түсіне сәйкес тапсырыс беріледі.',
            en: 'Pi-profile and Omega-profile combination is more budget-friendly option for panel fastening. Omega-profile is ordered in Pi-profile color according to RAL palette.', ar: 'يُعدّ الجمع بين القسم Pi والقسم Omega خياراً أكثر اقتصادية لتثبيت الألواح. يُطلب القسم Omega بلون القسم Pi وفقاً لمقياس ألوان RAL.'
          }
        },
        {
          title: { ru: 'Особенности монтажа', kz: 'Монтаж ерекшеліктері', en: 'Installation Features', ar: 'مميزات التركيب' },
          image: `${API_URL}/api/album3/17`,
          content: {
            ru: 'Декоративный Пи-профиль устанавливается в Омега-профиль с видимой стороны. При использовании допускаются асимметричные зазоры между профилями.',
            kz: 'Сәндік Пи-профиль Омега-профильге көрінетін жағынан орнатылады. Қолдану кезінде профильдер арасында асимметриялық саңылауларға рұқсат етіледі.',
            en: 'Decorative Pi-profile is installed in Omega-profile from visible side. When using, asymmetrical gaps between profiles are acceptable.', ar: 'يُركَّب القسم Pi الزخرفي داخل القسم Omega من الجهة المرئية. عند الاستخدام، تُقبل الفجوات غير المتماثلة بين الأقسام.'
          }
        }
      ]
    },
    // Перегородки (из Альбома №2)
    {
      id: 9,
      category: 'partitions',
      title: {
        ru: 'Перегородка в один слой',
        kz: 'Бір қабатты қалқа',
        en: 'Single-Layer Partition', ar: 'حاجز أحادي الطبقة'
      },
      description: {
        ru: 'Последовательность действий и виды профилей при монтаже перегородок из гипсовиниловых панелей.',
        kz: 'Гипсовинил панельдерінен қалқа орнату кезіндегі әрекеттер тізбегі мен профиль түрлері.',
        en: 'Sequence of actions and types of profiles for partition installation with gypsum-vinyl panels.', ar: 'تسلسل الأعمال وأنواع الأقسام المعدنية لتركيب الأقسام الداخلية بألواح الجبس فينيل.'
      },
      coverImage: `${API_URL}/api/album2/7`,
      slides: [
        {
          title: { ru: 'Последовательность монтажа', kz: 'Монтаж тізбегі', en: 'Installation Sequence', ar: 'تسلسل التركيب' },
          image: `${API_URL}/api/album2/5`,
          content: {
            ru: 'Последовательность действий при монтаже перегородки: подготовка пола, разметка стен и проёмов, монтаж металлокаркаса, утепление, установка плинтуса.',
            kz: 'Қалқа монтажы кезіндегі әрекеттер тізбегі: еденді дайындау, қабырғалар мен ойықтарды белгілеу, металл каркасты орнату, жылыту, плинтус орнату.',
            en: 'Sequence of actions for partition installation: floor preparation, wall and opening marking, metal frame installation, insulation, skirting board installation.', ar: 'تسلسل خطوات تركيب الحواجز: تجهيز الأرضية، وتحديد علامات الجدران والفتحات، وتركيب الهيكل المعدني، والعزل الحراري، وتركيب ألواح القاعدة.'
          }
        },
        {
          title: { ru: 'Виды профилей', kz: 'Профиль түрлері', en: 'Profile Types', ar: 'أنواع البروفيلات' },
          image: `${API_URL}/api/album2/6`,
          content: {
            ru: 'Виды профилей при монтаже перегородки: стоечный профиль (100x50мм, 75x50мм), L-профиль, F12 универсальный профиль, Омега-профиль.',
            kz: 'Қалқа монтажындағы профиль түрлері: тірек профилі (100x50мм, 75x50мм), L-профиль, F12 әмбебап профилі, Омега-профиль.',
            en: 'Profile types for partition installation: stud profile (100x50mm, 75x50mm), L-profile, F12 universal profile, Omega profile.', ar: 'أنواع البروفيلات لتركيب الحواجز: بروفيل الدعامات (100×50 مم، 75×50 مم)، البروفيل الزاوي L، البروفيل العالمي F12، بروفيل أوميغا.'
          }
        },
        {
          title: { ru: 'Монтаж металлокаркаса', kz: 'Металл каркас монтажы', en: 'Metal Frame Installation', ar: 'تركيب الهيكل المعدني' },
          image: `${API_URL}/api/album2/7`,
          content: {
            ru: 'Монтаж металлокаркаса для перегородки: подготовка пола, разметка линий, нарезка и крепление направляющих профилей.',
            kz: 'Қалқа үшін металл каркас монтажы: еденді дайындау, сызықтарды белгілеу, бағыттаушы профильдерді кесу және бекіту.',
            en: 'Metal frame installation for partitions: floor preparation, line marking, cutting and attaching guide profiles.', ar: 'تركيب الهيكل المعدني للحواجز: تجهيز الأرضية، ورسم الخطوط، وقطع وتثبيت البروفيلات الإرشادية.'
          }
        },
        {
          title: { ru: 'Установка стоечных профилей', kz: 'Тірек профильдерін орнату', en: 'Stud Profile Installation' },
          image: `${API_URL}/api/album2/8`,
          content: {
            ru: 'Использование акустической ленты для звукоизоляции, усиление дверных стоек деревянными брусками, установка горизонтальных перемычек.',
            kz: 'Дыбыс оқшаулау үшін акустикалық таспаны пайдалану, есік тіректерін ағаш брустармен күшейту, көлденең көпірлерді орнату.',
            en: 'Using acoustic tape for sound insulation, reinforcing door studs with wooden blocks, installing horizontal bracing.', ar: 'استخدام الشريط الصوتي للعزل الصوتي، وتعزيز قوائم الأبواب بكتل خشبية، وتركيب الدعامات الأفقية.'
          }
        },
        {
          title: { ru: 'Монтаж панелей на каркас', kz: 'Панельдерді каркасқа орнату', en: 'Panel Installation on Frame', ar: 'تركيب الألواح على الهيكل' },
          image: `${API_URL}/api/album2/9`,
          content: {
            ru: 'Установка панелей на каркас с использованием Омега-профилей и соединительных накладок.',
            kz: 'Омега-профильдер мен қосылыс жабындыларын пайдаланып панельдерді каркасқа орнату.',
            en: 'Installing panels on frame using Omega profiles and connecting covers.', ar: 'تركيب الألواح على الهيكل باستخدام بروفيلات أوميغا وأغطية التوصيل.'
          }
        },
        {
          title: { ru: 'Обшивка и утепление', kz: 'Қаптау және жылыту', en: 'Cladding and Insulation', ar: 'التكسية والعزل' },
          image: `${API_URL}/api/album2/10`,
          content: {
            ru: 'Обшивка одной стороны перегородки, прокладка коммуникаций, установка утеплителя перед обшивкой второй стороны.',
            kz: 'Қалқаның бір жағын қаптау, коммуникацияларды төсеу, екінші жағын қаптау алдында жылытқышты орнату.',
            en: 'Cladding one side of partition, laying communication lines, installing insulation before cladding the second side.', ar: 'تكسية أحد جانبي الحاجز، ومد خطوط الاتصالات، وتركيب العزل قبل تكسية الجانب الثاني.'
          }
        }
      ]
    },
    {
      id: 10,
      category: 'partitions',
      title: {
        ru: 'Перегородка в два слоя',
        kz: 'Екі қабатты қалқа',
        en: 'Two-Layer Partition', ar: 'الحاجز ثنائي الطبقات'
      },
      description: {
        ru: 'Технология монтажа перегородки в два слоя: гипсокартон + гипсовиниловые панели для повышенной прочности.',
        kz: 'Екі қабатты қалқа монтажының технологиясы: гипсокартон + гипсовинил панельдері беріктікті арттыру үшін.',
        en: 'Two-layer partition installation technology: gypsum board + gypsum-vinyl panels for increased strength.', ar: 'تقنية تركيب الحواجز ثنائية الطبقات: ألواح الجبس الكارتوني + ألواح الجبس الفينيل لتعزيز المتانة.'
      },
      coverImage: `${API_URL}/api/album2/12`,
      slides: [
        {
          title: { ru: 'Двухслойная система', kz: 'Екі қабатты жүйе', en: 'Two-Layer System', ar: 'نظام ثنائي الطبقات' },
          image: `${API_URL}/api/album2/11`,
          content: {
            ru: 'Двухслойная перегородка для повышенной прочности и звукоизоляции. Первый слой — гипсокартон, второй — панели. На такие перегородки можно вешать предметы без усиления каркаса.',
            kz: 'Беріктік пен дыбыс оқшаулауды арттыруға арналған екі қабатты қалқа. Бірінші қабат — гипсокартон, екіншісі — панельдер. Мұндай қалқаларға каркасты күшейтпей заттар ілуге болады.',
            en: 'Two-layer partition for increased strength and sound insulation. First layer is drywall, second is panels. Objects can be hung on these partitions without reinforcing the frame.', ar: 'حاجز مزدوج الطبقات لتعزيز المتانة والعزل الصوتي. الطبقة الأولى من الجبس الكارتوني، والطبقة الثانية من الألواح. يمكن تعليق الأجسام على هذه الحواجز دون الحاجة إلى تعزيز الهيكل.'
          }
        },
        {
          title: { ru: 'Монтаж первого слоя', kz: 'Бірінші қабатты орнату', en: 'First Layer Installation', ar: 'تركيب الطبقة الأولى' },
          image: `${API_URL}/api/album2/12`,
          content: {
            ru: 'Установка подсистемы, звукоизоляции и горизонтальный монтаж первого слоя гипсокартона с креплением к металлокаркасу.',
            kz: 'Қосалқы жүйені, дыбыс оқшаулауды орнату және бірінші гипсокартон қабатын металл каркасқа бекітіп көлденеңінен орнату.',
            en: 'Installation of subsystem, sound insulation, and horizontal mounting of first drywall layer with attachment to metal frame.', ar: 'تركيب النظام الفرعي والعزل الصوتي والتثبيت الأفقي لطبقة الجدار الجاف الأولى مع التثبيت بالإطار المعدني.'
          }
        },
        {
          title: { ru: 'Монтаж второго слоя', kz: 'Екінші қабатты орнату', en: 'Second Layer Installation', ar: 'تركيب الطبقة الثانية' },
          image: `${API_URL}/api/album2/13`,
          content: {
            ru: 'Установка панелей поверх гипсокартона. Омега-профиль крепится к стоечному профилю через гипсокартон. Стыки панелей смещаются относительно стыков гипсокартона.',
            kz: 'Панельдерді гипсокартон үстіне орнату. Омега-профиль гипсокартон арқылы тірек профиліне бекітіледі. Панель жіктері гипсокартон жіктеріне қатысты жылжытылады.',
            en: 'Installing panels on top of drywall. Omega profile is attached to stud profile through drywall. Panel joints are staggered relative to drywall joints.', ar: 'تركيب الألواح فوق الجدران الجافة. يتم تثبيت ملف Omega على ملف الدعامة عبر الجدار الجاف. تكون وصلات الألواح متعاكسة بالنسبة لوصلات الجدار الجاف.'
          }
        }
      ]
    },
    // Монтаж колонны (из Альбома №2)
    {
      id: 11,
      category: 'extra',
      title: {
        ru: 'Монтаж колонны',
        kz: 'Бағана монтажы',
        en: 'Column Installation', ar: 'تركيب الأعمدة'
      },
      description: {
        ru: 'Технические решения для облицовки колонн гипсовиниловыми панелями с использованием F-профилей.',
        kz: 'F-профильдерін пайдаланып бағаналарды гипсовинил панельдерімен қаптау үшін техникалық шешімдер.',
        en: 'Technical solutions for cladding columns with gypsum-vinyl panels using F-profiles.', ar: 'حلول تقنية لكسوة الأعمدة بألواح الجبس الفينيلي باستخدام ملفات F.'
      },
      coverImage: `${API_URL}/api/album2/14`,
      slides: [
        {
          title: { ru: 'Облицовка колонны', kz: 'Бағананы қаптау', en: 'Column Cladding', ar: 'كسوة الأعمدة' },
          image: `${API_URL}/api/album2/14`,
          content: {
            ru: 'Монтаж колонны с использованием каркаса и F-профилей. Панели соединяются на скрытой стороне колонны с помощью Омега-профилей, стык закрывается накладкой.',
            kz: 'Каркас пен F-профильдерін пайдаланып бағана монтажы. Панельдер бағананың жасырын жағында Омега-профильдер көмегімен қосылады, жік жабындымен жабылады.',
            en: 'Column installation using frame and F-profiles. Panels are connected on hidden side of column using Omega profiles, joint is covered with cap.', ar: 'تركيب الأعمدة باستخدام الإطار وملفات F. يتم توصيل الألواح على الجانب الخفي من العمود باستخدام ملفات Omega، ويُغطَّى الوصل بغطاء تشطيب.'
          }
        }
      ]
    },
    // Дверные арки (из Альбома №2)
    {
      id: 12,
      category: 'extra',
      title: {
        ru: 'Монтаж дверной арки',
        kz: 'Есік аркасының монтажы',
        en: 'Door Arch Installation', ar: 'تركيب قوس الباب'
      },
      description: {
        ru: 'Технология обрамления дверных проёмов и арок гипсовиниловыми панелями.',
        kz: 'Есік ойықтары мен аркаларды гипсовинил панельдерімен шегендеу технологиясы.',
        en: 'Technology for framing door openings and arches with gypsum-vinyl panels.', ar: 'تقنية تأطير مداخل الأبواب والأقواس بألواح الجبس الفينيلي.'
      },
      coverImage: `${API_URL}/api/album2/15`,
      slides: [
        {
          title: { ru: 'Схема дверной арки', kz: 'Есік аркасының схемасы', en: 'Door Arch Diagram', ar: 'مخطط قوس الباب' },
          image: `${API_URL}/api/album2/15`,
          content: {
            ru: 'Схематичная иллюстрация монтажа дверной арки: каркас, обшивка панелями и отделочные элементы.',
            kz: 'Есік аркасын орнатудың схемалық иллюстрациясы: каркас, панельдермен қаптау және әрлеу элементтері.',
            en: 'Schematic illustration of door arch installation: frame, panel cladding, and finishing elements.', ar: 'رسم تخطيطي توضيحي لتركيب قوس الباب: الهيكل، وتكسية الألواح، والعناصر التشطيبية.'
          }
        },
        {
          title: { ru: 'Отделка проёма', kz: 'Ойықты әрлеу', en: 'Opening Finishing', ar: 'تشطيب المدخل' },
          image: `${API_URL}/api/album2/16`,
          content: {
            ru: 'Метод отделки дверного проёма, где одна сторона с оштукатуренными стенами, а другая — с панелями. F-профили устанавливаются сверху стены и арки.',
            kz: 'Бір жағы сыланған қабырғалармен, екінші жағы панельдермен болатын есік ойығын әрлеу әдісі. F-профильдер қабырға мен арканың үстіне орнатылады.',
            en: 'Method for finishing doorway where one side has plastered walls and other has panels. F-profiles are applied to top of wall and arch.', ar: 'طريقة لتشطيب مدخل الباب حيث يكون أحد الجانبين بجدران مجصصة والآخر بألواح. تُثبَّت ملفات F على الجزء العلوي من الجدار والقوس.'
          }
        },
        {
          title: { ru: 'Установка профилей', kz: 'Профильдерді орнату', en: 'Profile Installation', ar: 'تركيب الملفات' },
          image: `${API_URL}/api/album2/17`,
          content: {
            ru: 'Крепление F-профилей к вертикальным сторонам арки, установка L-профиля сверху, затем крепление панелей в пазы F-профиля.',
            kz: 'F-профильдерді арканың тік жақтарына бекіту, үстіне L-профиль орнату, содан кейін панельдерді F-профиль ойықтарына бекіту.',
            en: 'Attaching F-profiles to vertical sides of arch, fitting L-profile at top, then attaching panels into F-profile grooves.', ar: 'تركيب ملفات F على الجوانب الرأسية للقوس، وتثبيت ملف L في الجزء العلوي، ثم إدخال الألواح في أخاديد ملفات F.'
          }
        },
        {
          title: { ru: 'Завершение монтажа', kz: 'Монтажды аяқтау', en: 'Finishing Installation', ar: 'التركيب النهائي' },
          image: `${API_URL}/api/album2/18`,
          content: {
            ru: 'Финальные шаги: панели устанавливаются на лицевую и боковые стороны арки, Омега-профиль используется для соединения на скрытой стороне.',
            kz: 'Соңғы қадамдар: панельдер арканың алдыңғы және бүйір жақтарына орнатылады, Омега-профиль жасырын жағында қосылу үшін қолданылады.',
            en: 'Final steps: panels are fitted to front and sides of arch, Omega profile used for joining on hidden side.', ar: 'الخطوات النهائية: يتم تركيب الألواح على الواجهة والجوانب الجانبية للقوس، ويُستخدم ملف أوميغا للربط على الجانب المخفي.'
          }
        }
      ]
    },
    // Наращивание высоты (из Альбома №2)
    {
      id: 13,
      category: 'extra',
      title: {
        ru: 'Наращивание высоты панелями',
        kz: 'Панельдермен биіктікті арттыру',
        en: 'Height Extension with Panels', ar: 'تمديد الارتفاع بالألواح'
      },
      description: {
        ru: 'Решения для наращивания высоты стен при помощи дополнительных панелей и горизонтальных Омега-профилей.',
        kz: 'Қосымша панельдер мен көлденең Омега-профильдер көмегімен қабырғалар биіктігін арттыру шешімдері.',
        en: 'Solutions for wall height extension using additional panels and horizontal Omega profiles.', ar: 'حلول لتمديد ارتفاع الجدار باستخدام ألواح إضافية وملفات أوميغا أفقية.'
      },
      coverImage: `${API_URL}/api/album2/19`,
      slides: [
        {
          title: { ru: 'Наращивание высоты', kz: 'Биіктікті арттыру', en: 'Height Extension', ar: 'تمديد الارتفاع' },
          image: `${API_URL}/api/album2/19`,
          content: {
            ru: 'Если стандартной высоты панели недостаточно, используются дополнительные горизонтальные Омега-профили. Также панели можно нарезать на блоки для дизайнерских решений.',
            kz: 'Егер панельдің стандартты биіктігі жеткіліксіз болса, қосымша көлденең Омега-профильдер қолданылады. Сондай-ақ панельдерді дизайнерлік шешімдер үшін блоктарға кесуге болады.',
            en: 'If standard panel height is insufficient, additional horizontal Omega profiles are used. Panels can also be cut into blocks for design purposes.', ar: 'إذا كان ارتفاع اللوح القياسي غير كافٍ، تُستخدم ملفات أوميغا أفقية إضافية. كما يمكن تقطيع الألواح إلى كتل لأغراض تصميمية.'
          }
        }
      ]
    },
    // Примыкание к полу (из Альбома №1)
    {
      id: 14,
      category: 'extra',
      title: {
        ru: 'Примыкание к полу',
        kz: 'Еденге жапсару',
        en: 'Floor Junction', ar: 'تقاطع الأرضية'
      },
      description: {
        ru: 'Варианты оформления стыка панелей с полом: L-профиль, плинтус с кабель-каналом, теневой плинтус.',
        kz: 'Панельдерді еденмен жалғау нұсқалары: L-профиль, кабель-каналы бар плинтус, көлеңке плинтус.',
        en: 'Options for panel-floor junction: L-profile, skirting with cable channel, shadow skirting.', ar: 'خيارات تقاطع اللوح مع الأرضية: ملف L، قاعدة تشطيب مع قناة كابلات، قاعدة تشطيب ظلية.'
      },
      coverImage: `${API_URL}/api/album1/14`,
      slides: [
        {
          title: { ru: 'L-профиль как плинтус', kz: 'L-профиль плинтус ретінде', en: 'L-Profile as Skirting', ar: 'قاعدة L كحافة تجميلية' },
          image: `${API_URL}/api/album1/14`,
          content: {
            ru: 'L-профиль используется как декоративный плинтус. Монтаж панелей начинается только после завершения чистовой отделки пола.',
            kz: 'L-профиль сәндік плинтус ретінде қолданылады. Панельдерді орнату тек еденнің таза әрлеуі аяқталғаннан кейін басталады.',
            en: 'L-profile is used as decorative skirting. Panel installation begins only after floor finishing is complete.', ar: 'تُستخدم قاعدة L كحافة زخرفية. يبدأ تركيب الألواح فقط بعد الانتهاء من تشطيب الأرضية.'
          }
        },
        {
          title: { ru: 'Плинтус с кабель-каналом', kz: 'Кабель-каналы бар плинтус', en: 'Skirting with Cable Channel', ar: 'قاعدة التغطية مع قناة الكابل' },
          image: `${API_URL}/api/album1/15`,
          content: {
            ru: 'Накладной плинтус с кабель-каналом крепится на клей или длинные саморезы. Удобен для скрытой прокладки проводов.',
            kz: 'Кабель-каналы бар үстіңгі плинтус желімге немесе ұзын бұрандаларға бекітіледі. Сымдарды жасырын төсеуге ыңғайлы.',
            en: 'Overlay skirting with cable channel is attached with glue or long screws. Convenient for hidden cable routing.', ar: 'قاعدة التغطية مع قناة الكابل تُثبَّت بالغراء أو بمسامير طويلة. مناسبة لتوجيه الكابلات بشكل مخفي.'
          }
        },
        {
          title: { ru: 'Теневой плинтус', kz: 'Көлеңке плинтус', en: 'Shadow Skirting', ar: 'الحافة الظلية' },
          image: `${API_URL}/api/album1/16`,
          content: {
            ru: 'Теневой плинтус создаёт эффект "парящей стены". Может включать LED-подсветку. Защищает панели при уборке пола.',
            kz: 'Көлеңке плинтус "қалқыған қабырға" әсерін жасайды. LED-жарық қосуға болады. Еденді жинау кезінде панельдерді қорғайды.',
            en: 'Shadow skirting creates a "floating wall" effect. Can include LED lighting. Protects panels during floor cleaning.', ar: 'الحافة الظلية تُوجد تأثير "الجدار العائم". يمكن أن تتضمن إضاءة LED. تحمي الألواح أثناء تنظيف الأرضية.'
          }
        }
      ]
    },
    // Примыкание к потолку (из Альбома №1)
    {
      id: 15,
      category: 'extra',
      title: {
        ru: 'Примыкание к потолку',
        kz: 'Төбеге жапсару',
        en: 'Ceiling Junction', ar: 'تقاطع السقف'
      },
      description: {
        ru: 'Варианты оформления стыка панелей с потолком с использованием L-профиля и Омега-профиля.',
        kz: 'L-профиль және Омега-профиль пайдаланып панельдерді төбемен жалғау нұсқалары.',
        en: 'Options for panel-ceiling junction using L-profile and Omega profile.', ar: 'خيارات لتقاطع الألواح بالسقف باستخدام قاعدة L وقاعدة أوميغا.'
      },
      coverImage: `${API_URL}/api/album1/17`,
      slides: [
        {
          title: { ru: 'Потолочный плинтус из L-профиля', kz: 'L-профильден төбе плинтусы', en: 'Ceiling Skirting from L-Profile', ar: 'قرنيش السقف من قابل الـ L' },
          image: `${API_URL}/api/album1/17`,
          content: {
            ru: 'При наличии подвесного или гипсокартонного потолка L-профиль крепится к потолку на жидкие гвозди, затем вставляется панель.',
            kz: 'Іліспелі немесе гипсокартон төбе болған кезде L-профиль төбеге сұйық шегелерге бекітіледі, содан кейін панель салынады.',
            en: 'With suspended or drywall ceiling, L-profile is attached to ceiling with liquid nails, then panel is inserted.', ar: 'في حالة الأسقف المعلقة أو أسقف الجدران الجافة، يُثبَّت قابل الـ L بالسقف باستخدام الغراء السائل، ثم يُدرج اللوح في مكانه.'
          }
        },
        {
          title: { ru: 'Омега-профиль на потолке', kz: 'Төбедегі Омега-профиль', en: 'Omega Profile at Ceiling', ar: 'قابل أوميغا عند السقف' },
          image: `${API_URL}/api/album1/18`,
          content: {
            ru: 'Омега-профиль позволяет делать панели съёмными. Омега-профиль прикручивается к стоечному профилю, декоративный профиль защёлкивается.',
            kz: 'Омега-профиль панельдерді алынбалы етуге мүмкіндік береді. Омега-профиль тірек профиліне бұралады, сәндік профиль бекітіледі.',
            en: 'Omega profile allows panels to be removable. Omega profile is screwed to stud profile, decorative profile clips in.', ar: 'يتيح قابل أوميغا إمكانية فك الألواح وإعادة تركيبها. يُثبَّت قابل أوميغا بالبراغي على قابل القائمة، ويُشبَّك القابل الزخرفي في مكانه.'
          }
        }
      ]
    },
    // Внутренний угол (из Альбома №1)
    {
      id: 16,
      category: 'corners',
      title: {
        ru: 'Внутренний угол',
        kz: 'Ішкі бұрыш',
        en: 'Internal Corner', ar: 'الزاوية الداخلية'
      },
      description: {
        ru: 'Формирование внутренних углов 90° с использованием L-профилей различными способами.',
        kz: 'L-профильдерді пайдаланып әртүрлі тәсілдермен 90° ішкі бұрыштарды қалыптастыру.',
        en: 'Forming 90° internal corners using L-profiles in various ways.', ar: 'تشكيل الزوايا الداخلية 90° باستخدام قوابل على شكل حرف L بأساليب متعددة.'
      },
      coverImage: `${API_URL}/api/album1/19`,
      slides: [
        {
          title: { ru: 'Варианты внутреннего угла', kz: 'Ішкі бұрыш нұсқалары', en: 'Internal Corner Options', ar: 'خيارات الزاوية الداخلية' },
          image: `${API_URL}/api/album1/19`,
          content: {
            ru: 'Внутренний угол 90° формируется с помощью L-профилей. L-профиль крепится исключительно к стойкам металлокаркаса саморезами с шагом 400-600мм.',
            kz: '90° ішкі бұрыш L-профильдер көмегімен қалыптастырылады. L-профиль тек металл каркас тіректеріне 400-600мм қадаммен бұрандалармен бекітіледі.',
            en: '90° internal corner is formed using L-profiles. L-profile is attached exclusively to metal frame studs with screws at 400-600mm intervals.', ar: 'يتم تشكيل الزاوية الداخلية 90° باستخدام قوابل على شكل حرف L. يُثبَّت قابل الـ L حصرياً على قوائم الهيكل المعدني بواسطة براغي على فترات 400-600 مم.'
          }
        },
        {
          title: { ru: 'Детальные узлы', kz: 'Егжей-тегжейлі түйіндер', en: 'Detailed Nodes', ar: 'العقد التفصيلية' },
          image: `${API_URL}/api/album1/20`,
          content: {
            ru: 'Два способа формирования внутреннего угла с L-профилями. Выбор способа влияет на расположение направляющих профилей.',
            kz: 'L-профильдермен ішкі бұрышты қалыптастырудың екі тәсілі. Тәсілді таңдау бағыттаушы профильдердің орналасуына әсер етеді.',
            en: 'Two methods for forming internal corner with L-profiles. Method choice affects guide profile placement.', ar: 'طريقتان لتشكيل الزاوية الداخلية بقوابل L. يؤثر اختيار الطريقة على موضع قابل التوجيه.'
          }
        },
        {
          title: { ru: 'Два L-профиля', kz: 'Екі L-профиль', en: 'Two L-Profiles', ar: 'قابلان على شكل L' },
          image: `${API_URL}/api/album1/21`,
          content: {
            ru: 'Два L-профиля для внутреннего угла. Панели не прижимаются вплотную к профилям — оставляется технологический зазор 1-2мм для теплового расширения.',
            kz: 'Ішкі бұрыш үшін екі L-профиль. Панельдер профильдерге тығыз басылмайды — жылулық кеңею үшін 1-2мм технологиялық саңылау қалдырылады.',
            en: 'Two L-profiles for internal corner. Panels are not pressed tight to profiles — 1-2mm technological gap is left for thermal expansion.', ar: 'يُستخدم قابلان على شكل L للزاوية الداخلية. لا تُضغط الألواح بإحكام على القوابل — بل يُترك فراغ تقني مقداره 1-2 مم لاستيعاب التمدد الحراري.'
          }
        }
      ]
    },
    // Наружный угол (из Альбома №1)
    {
      id: 17,
      category: 'corners',
      title: {
        ru: 'Наружный угол',
        kz: 'Сыртқы бұрыш',
        en: 'External Corner', ar: 'الزاوية الخارجية'
      },
      description: {
        ru: 'Оформление наружных углов 90° с использованием F-профилей.',
        kz: 'F-профильдерді пайдаланып 90° сыртқы бұрыштарды безендіру.',
        en: 'Finishing 90° external corners using F-profiles.', ar: 'تشطيب الزوايا الخارجية 90° باستخدام قوابل F.'
      },
      coverImage: `${API_URL}/api/album1/22`,
      slides: [
        {
          title: { ru: 'Наружный угол с F-профилем', kz: 'F-профильмен сыртқы бұрыш', en: 'External Corner with F-Profile', ar: 'الزاوية الخارجية بقابل F' },
          image: `${API_URL}/api/album1/22`,
          content: {
            ru: 'Наружный угол 90° формируется с помощью F-профилей. Профиль крепится к стойкам металлокаркаса саморезами с шагом не более 400-600мм.',
            kz: '90° сыртқы бұрыш F-профильдер көмегімен қалыптастырылады. Профиль металл каркас тіректеріне 400-600мм-ден аспайтын қадаммен бұрандалармен бекітіледі.',
            en: '90° external corner is formed using F-profiles. Profile is attached to metal frame studs with screws at intervals not exceeding 400-600mm.', ar: 'يتم تشكيل الزاوية الخارجية 90° باستخدام قوابل F. يُثبَّت القابل على دعامات الإطار المعدني بمسامير بفواصل لا تتجاوز 400-600 مم.'
          }
        },
        {
          title: { ru: 'Типы F-профилей', kz: 'F-профиль түрлері', en: 'F-Profile Types', ar: 'أنواع ملف F' },
          image: `${API_URL}/api/album1/23`,
          content: {
            ru: 'Детальные чертежи двух типов F-профилей (F12, F26) для наружных углов с указанием размеров и способов крепления.',
            kz: 'Өлшемдер мен бекіту тәсілдерін көрсететін сыртқы бұрыштарға арналған F-профильдердің (F12, F26) екі түрінің егжей-тегжейлі сызбалары.',
            en: 'Detailed drawings of two F-profile types (F12, F26) for external corners showing dimensions and mounting methods.', ar: 'رسومات تفصيلية لنوعين من ملف F (F12، F26) للزوايا الخارجية تُظهر الأبعاد وطرق التثبيت.'
          }
        }
      ]
    },
    // Оконный откос (из Альбома №1)
    {
      id: 18,
      category: 'corners',
      title: {
        ru: 'Оконный откос',
        kz: 'Терезе еңісі',
        en: 'Window Slope', ar: 'منحدر النافذة'
      },
      description: {
        ru: 'Технология отделки оконных откосов с использованием F-профиля и герметика.',
        kz: 'F-профиль және тығыздағышты пайдаланып терезе еңістерін әрлеу технологиясы.',
        en: 'Window slope finishing technology using F-profile and sealant.', ar: 'تقنية تشطيب منحدرات النوافذ باستخدام ملف F ومانع التسرب.'
      },
      coverImage: `${API_URL}/api/album1/24`,
      slides: [
        {
          title: { ru: 'Монтаж откоса', kz: 'Еңісті орнату', en: 'Slope Installation', ar: 'تركيب المنحدر' },
          image: `${API_URL}/api/album1/24`,
          content: {
            ru: 'Монтаж начинается от окна к наружному углу. На панель для откоса наносится клей, она прижимается к стене. Затем F-профиль устанавливается на край панели.',
            kz: 'Монтаж терезеден сыртқы бұрышқа қарай басталады. Еңіс панеліне желім жағылады, ол қабырғаға басылады. Содан кейін F-профиль панель шетіне орнатылады.',
            en: 'Installation starts from window to external corner. Glue is applied to slope panel, it is pressed to wall. Then F-profile is installed on panel edge.', ar: 'يبدأ التركيب من النافذة باتجاه الزاوية الخارجية. يُوضع الغراء على لوح المنحدر ويُضغط على الحائط، ثم يُركَّب ملف F على حافة اللوح.'
          }
        },
        {
          title: { ru: 'Завершение откоса', kz: 'Еңісті аяқтау', en: 'Completing Slope', ar: 'إتمام تركيب المنحدر' },
          image: `${API_URL}/api/album1/25`,
          content: {
            ru: 'Следующая панель вставляется в F-профиль и крепится Омега-профилем. Рекомендуется нанести прозрачный герметик на стык панели, откоса и окна.',
            kz: 'Келесі панель F-профильге салынып, Омега-профильмен бекітіледі. Панель, еңіс және терезе жігіне мөлдір тығыздағыш жағу ұсынылады.',
            en: 'Next panel is inserted into F-profile and attached with Omega profile. Apply transparent sealant to joint of panel, slope, and window.', ar: 'يُدرج اللوح التالي في ملف F ويُثبَّت بملف أوميغا. ضع مانعًا للتسرب شفافًا عند تقاطع اللوح ومنحدر النافذة والنافذة.'
          }
        }
      ]
    },
    // Дверной блок (из Альбома №1)
    {
      id: 19,
      category: 'corners',
      title: {
        ru: 'Дверной блок',
        kz: 'Есік блогы',
        en: 'Door Block', ar: 'كتلة الباب'
      },
      description: {
        ru: 'Варианты установки панелей вокруг дверных проёмов: скрытый монтаж и монтаж с наличниками.',
        kz: 'Есік ойықтарының айналасына панельдерді орнату нұсқалары: жасырын монтаж және тақтайшалармен монтаж.',
        en: 'Options for installing panels around door openings: hidden mounting and mounting with architraves.', ar: 'خيارات تركيب الألواح حول فتحات الأبواب: التثبيت المخفي والتثبيت مع الإطارات.'
      },
      coverImage: `${API_URL}/api/album1/26`,
      slides: [
        {
          title: { ru: 'Скрытый монтаж двери', kz: 'Есіктің жасырын монтажы', en: 'Hidden Door Installation', ar: 'تركيب الباب المخفي' },
          image: `${API_URL}/api/album1/26`,
          content: {
            ru: 'Скрытый монтаж двери без наличников. Панели крепятся к дверной коробке с помощью L-профиля, прикрученного к коробке.',
            kz: 'Тақтайшаларсыз есіктің жасырын монтажы. Панельдер есік қорабына L-профиль арқылы бекітіледі, ол қорапқа бұралады.',
            en: 'Hidden door installation without architraves. Panels are attached to door frame using L-profile screwed to frame.', ar: 'تركيب الباب المخفي بدون إطارات. تُثبَّت الألواح على إطار الباب باستخدام ملف على شكل حرف L مثبَّت بالبراغي على الإطار.'
          }
        },
        {
          title: { ru: 'Монтаж с наличниками', kz: 'Тақтайшалармен монтаж', en: 'Installation with Architraves', ar: 'التركيب مع الإطارات' },
          image: `${API_URL}/api/album1/27`,
          content: {
            ru: 'Край панели приклеивается и прикручивается к стоечному профилю. Наличники устанавливаются поверх панелей.',
            kz: 'Панель шеті желімделіп, тірек профиліне бұралады. Тақтайшалар панельдер үстіне орнатылады.',
            en: 'Panel edge is glued and screwed to stud profile. Architraves are installed over panels.', ar: 'يُلصق حافة اللوح ويُثبَّت بالبراغي على الملف الحاملة. تُركَّب الإطارات فوق الألواح.'
          }
        },
        {
          title: { ru: 'Сравнение вариантов', kz: 'Нұсқаларды салыстыру', en: 'Options Comparison', ar: 'مقارنة الخيارات' },
          image: `${API_URL}/api/album1/28`,
          content: {
            ru: 'Визуальное сравнение двух вариантов: скрытый монтаж (без наличников) и классический (с наличниками).',
            kz: 'Екі нұсқаны визуалды салыстыру: жасырын монтаж (тақтайшаларсыз) және классикалық (тақтайшалармен).',
            en: 'Visual comparison of two options: hidden installation (without architraves) and classic (with architraves).', ar: 'مقارنة بصرية بين خيارَين: التركيب المخفي (بدون إطارات) والكلاسيكي (مع إطارات).'
          }
        },
        {
          title: { ru: 'Наружный угол в проёме', kz: 'Ойықтағы сыртқы бұрыш', en: 'External Corner in Opening', ar: 'الزاوية الخارجية في الفتحة' },
          image: `${API_URL}/api/album1/29`,
          content: {
            ru: 'Формирование наружного угла в дверном проёме с помощью F-профиля. L-профиль крепится к направляющему профилю, затем F-профиль.',
            kz: 'F-профиль көмегімен есік ойығында сыртқы бұрышты қалыптастыру. L-профиль бағыттаушы профильге бекітіледі, содан кейін F-профиль.',
            en: 'Forming external corner in doorway using F-profile. L-profile is attached to guide profile, then F-profile.', ar: 'تشكيل الزاوية الخارجية في مدخل الباب باستخدام ملف F. يُثبَّت ملف L على ملف التوجيه، ثم ملف F.'
          }
        },
        {
          title: { ru: 'Завершение проёма', kz: 'Ойықты аяқтау', en: 'Finishing Opening', ar: 'تشطيب الفتحة' },
          image: `${API_URL}/api/album1/30`,
          content: {
            ru: 'Следующая панель вклеивается в F-профиль, а стык закрывается Омега-профилем с крышкой.',
            kz: 'Келесі панель F-профильге желімделеді, ал жік Омега-профильмен және қақпақпен жабылады.',
            en: 'Next panel is glued into F-profile, and joint is covered with Omega profile and cap.', ar: 'يُلصق اللوح التالي داخل ملف F، ويُغطى الوصل بملف أوميغا والغطاء.'
          }
        }
      ]
    },
    // Рекомендации по монтажу (из Альбома №1)
    {
      id: 20,
      category: 'prep',
      title: {
        ru: 'Рекомендации по монтажу',
        kz: 'Монтаж бойынша ұсыныстар',
        en: 'Installation Recommendations', ar: 'توصيات التركيب'
      },
      description: {
        ru: 'Полезные советы и рекомендации для качественного монтажа панелей.',
        kz: 'Панельдерді сапалы орнату үшін пайдалы кеңестер мен ұсыныстар.',
        en: 'Useful tips and recommendations for quality panel installation.', ar: 'نصائح وتوصيات مفيدة لتركيب الألواح بجودة عالية.'
      },
      coverImage: `${API_URL}/api/album1/31`,
      slides: [
        {
          title: { ru: 'Общие рекомендации', kz: 'Жалпы ұсыныстар', en: 'General Recommendations', ar: 'التوصيات العامة' },
          image: `${API_URL}/api/album1/31`,
          content: {
            ru: 'Начинайте со сложных участков. Используйте разметочные линии для точного попадания саморезов. Применяйте жидкие гвозди, клей PERLFIX и малярный скотч.',
            kz: 'Күрделі аймақтардан бастаңыз. Бұрандалардың дәл түсуі үшін белгілеу сызықтарын пайдаланыңыз. Сұйық шегелер, PERLFIX желімі және бояушы скотч қолданыңыз.',
            en: 'Start with complex areas. Use marking lines for accurate screw placement. Apply liquid nails, PERLFIX glue, and masking tape.', ar: 'ابدأ بالمناطق المعقدة. استخدم خطوط التحديد لضبط موضع المسامير بدقة. طبّق الغراء السائل وغراء PERLFIX وشريط التغطية.'
          }
        },
        {
          title: { ru: 'Резка и обработка', kz: 'Кесу және өңдеу', en: 'Cutting and Processing', ar: 'القطع والمعالجة' },
          image: `${API_URL}/api/album1/32`,
          content: {
            ru: 'Метод резки панелей ножом и контролируемым сломом. Очистка поверхностей, защита коммуникаций гофрой, вырезание отверстий под розетки.',
            kz: 'Панельдерді пышақпен және бақыланатын сынумен кесу әдісі. Беттерді тазарту, коммуникацияларды гофрамен қорғау, розеткаларға тесіктер кесу.',
            en: 'Panel cutting method with knife and controlled break. Surface cleaning, utility protection with corrugated sleeve, cutting openings for sockets.', ar: 'طريقة قطع الألواح بالسكين والكسر المتحكَّم به. تنظيف السطح، وحماية التمديدات بالأنبوب المموَّج، وقطع فتحات المقابس الكهربائية.'
          }
        },
        {
          title: { ru: 'Усиление и инструменты', kz: 'Күшейту және құралдар', en: 'Reinforcement and Tools', ar: 'التعزيز والأدوات' },
          image: `${API_URL}/api/album1/33`,
          content: {
            ru: 'Усиление каркаса для тяжёлых нагрузок (дверные проёмы, крепления). Список необходимых инструментов, клеёв, герметиков и саморезов.',
            kz: 'Ауыр жүктемелер үшін каркасты күшейту (есік ойықтары, бекіткіштер). Қажетті құралдар, желімдер, тығыздағыштар және бұрандалар тізімі.',
            en: 'Frame reinforcement for heavy loads (doorways, mountings). List of required tools, adhesives, sealants, and screws.', ar: 'تعزيز الهيكل للأحمال الثقيلة (فتحات الأبواب، نقاط التثبيت). قائمة الأدوات والمواد اللاصقة والمانعات للتسرب والمسامير اللازمة.'
          }
        }
      ]
    },
    // Монтаж каркаса (из Альбома тех. решений №1)
    {
      id: 21,
      category: 'frame',
      title: {
        ru: 'Установка направляющих и подвесов',
        kz: 'Бағыттауыштар мен аспаларды орнату',
        en: 'Guide Profiles and Hangers Installation', ar: 'تركيب الملفات الموجِّهة والمعلَّقات'
      },
      description: {
        ru: 'Разметка стен, установка направляющих профилей (ПН) и прямых подвесов. Формирование первого внутреннего угла каркаса.',
        kz: 'Қабырғаларды белгілеу, бағыттаушы профильдерді (ПН) және тікелей аспаларды орнату. Каркастың бірінші ішкі бұрышын қалыптастыру.',
        en: 'Wall marking, guide profile (PN) and direct hanger installation. Forming the first internal corner of the frame.', ar: 'تحديد محاور الجدران، وتركيب الملف الموجِّه (PN) والمعلَّقات المباشرة. تشكيل الزاوية الداخلية الأولى للهيكل.'
      },
      coverImage: `${API_URL}/api/album1/8`,
      slides: [
        {
          title: { ru: 'Разметка и направляющие профили', kz: 'Белгілеу және бағыттаушы профильдер', en: 'Marking and Guide Profiles', ar: 'التحديد والملفات الموجِّهة' },
          image: `${API_URL}/api/album1/7`,
          content: {
            ru: 'Первый этап — разметка стен и установка направляющих профилей (ПН) на пол и потолок. Направляющие задают плоскость будущей облицовки. Шаг крепления к основанию — 600 мм.',
            kz: 'Бірінші кезең — қабырғаларды белгілеу және еденге мен төбеге бағыттаушы профильдерді (ПН) орнату. Бағыттаушылар болашақ қаптаманың жазықтығын анықтайды. Негізге бекіту қадамы — 600 мм.',
            en: 'First stage — wall marking and guide profile (PN) installation on floor and ceiling. Guides define the plane of the future cladding. Fastening step to the base — 600 mm.', ar: 'المرحلة الأولى — تحديد محاور الجدران وتركيب الملف الموجِّه (PN) على الأرضية والسقف. تُحدِّد الموجِّهات مستوى الكسوة المستقبلية. خطوة التثبيت بالقاعدة — 600 مم.'
          }
        },
        {
          title: { ru: 'Установка подвесов и формирование угла', kz: 'Аспаларды орнату және бұрышты қалыптастыру', en: 'Hanger Installation and Corner Formation', ar: 'تركيب المعلقات وتشكيل الزاوية' },
          image: `${API_URL}/api/album1/8`,
          content: {
            ru: 'Второй этап — формирование внутреннего угла из стоечных профилей. Прямые подвесы крепятся к стене с шагом 400–600 мм. Стоечный профиль крепится к подвесу саморезами с контролем вертикальности по уровню.',
            kz: 'Екінші кезең — тіреу профильдерінен ішкі бұрышты қалыптастыру. Тікелей аспалар қабырғаға 400-600 мм қадаммен бекітіледі. Тіреу профилі аспаға бұрандалармен деңгейбойынша тіктікті бақылаумен бекітіледі.',
            en: 'Second stage — forming internal corner from stud profiles. Direct hangers are attached to the wall at 400–600 mm intervals. Stud profile is fastened to the hanger with screws, checking vertical alignment with a level.', ar: 'المرحلة الثانية — تشكيل الزاوية الداخلية من بروفيلات الدعامة. تُثبَّت المعلقات المباشرة على الجدار بفواصل 400–600 مم. يُربط بروفيل الدعامة بالمعلقة بواسطة براغٍ، مع التحقق من الاستواء الرأسي باستخدام الميزان.'
          }
        }
      ]
    },
    {
      id: 22,
      category: 'frame',
      title: {
        ru: 'Монтаж стоечных профилей',
        kz: 'Тіреу профильдерін монтаждау',
        en: 'Stud Profile Installation', ar: 'تركيب بروفيلات الدعامة'
      },
      description: {
        ru: 'Расстановка промежуточных стоечных профилей, крепление L- и Омега-профилей для фиксации панелей.',
        kz: 'Аралық тіреу профильдерін орналастыру, панельдерді бекіту үшін L- және Омега-профильдерін орнату.',
        en: 'Intermediate stud profile placement, L- and Omega-profile attachment for panel fixing.', ar: 'تحديد موضع بروفيل الدعامة الوسيطة، وتثبيت البروفيل L وبروفيل أوميغا لتثبيت الألواح.'
      },
      coverImage: `${API_URL}/api/album1/10`,
      slides: [
        {
          title: { ru: 'Расстановка стоечных профилей', kz: 'Тіреу профильдерін орналастыру', en: 'Stud Profile Layout', ar: 'تخطيط بروفيلات الدعامة' },
          image: `${API_URL}/api/album1/9`,
          content: {
            ru: 'На L-профиль (профиль 1) устанавливается панель, которая прижимается Омега-профилем к стоечному профилю каркаса (профиль 4). Следующая панель заходит в Омега-профиль и фиксируется следующим Омега-профилем. Дальнейшее крепление аналогично.',
            kz: 'L-профильге (1-профиль) панель орнатылады, ол Омега-профильмен каркастың тіреу профиліне (4-профиль) қысылады. Келесі панель Омега-профильге кіреді және келесі Омега-профильмен бекітіледі.',
            en: 'Panel is installed on L-profile (profile 1), pressed by Omega-profile against the frame stud profile (profile 4). Next panel enters the Omega-profile and is fixed by the following Omega-profile. Further fastening follows the same pattern.', ar: 'يُركَّب اللوح على البروفيل L (البروفيل 1)، ويُضغط بواسطة بروفيل أوميغا على بروفيل دعامة الإطار (البروفيل 4). يدخل اللوح التالي في بروفيل أوميغا ويُثبَّت بواسطة بروفيل أوميغا اللاحق. يتبع التثبيت الإضافي النمط ذاته.'
          }
        },
        {
          title: { ru: 'Промежуточные профили и крепление', kz: 'Аралық профильдер және бекіту', en: 'Intermediate Profiles and Fastening', ar: 'البروفيلات الوسيطة والتثبيت' },
          image: `${API_URL}/api/album1/10`,
          content: {
            ru: 'Дополнительные стоечные профили (профили 2, 3, 5, 6) устанавливаются для опоры панелей. Место крепления Омега-профиля определяет стык между панелями. При монтаже второго внутреннего угла сначала устанавливается L-профиль, затем крайний Омега-профиль.',
            kz: 'Қосымша тіреу профильдері (2, 3, 5, 6 профильдер) панельдерге тірек үшін орнатылады. Омега-профильді бекіту орны панельдер арасындағы жікті анықтайды.',
            en: 'Additional stud profiles (profiles 2, 3, 5, 6) are installed for panel support. Omega-profile attachment point defines the joint between panels. For the second internal corner, first install L-profile, then the edge Omega-profile.', ar: 'يتم تركيب بروفيلات الدعامة الإضافية (البروفيلات 2، 3، 5، 6) لدعم الألواح. تحدد نقطة تثبيت البروفيل أوميغا موضع الوصلة بين الألواح. بالنسبة للزاوية الداخلية الثانية، يُركَّب البروفيل L أولاً، ثم بروفيل أوميغا الطرفي.'
          }
        }
      ]
    },
    {
      id: 23,
      category: 'frame',
      title: {
        ru: 'Узлы внутреннего угла каркаса',
        kz: 'Каркастың ішкі бұрыш тораптары',
        en: 'Frame Internal Corner Nodes', ar: 'عقد الزوايا الداخلية للهيكل'
      },
      description: {
        ru: 'Варианты формирования внутренних углов из стоечных профилей. Узлы крепления L-профиля в углах.',
        kz: 'Тіреу профильдерінен ішкі бұрыштарды қалыптастыру нұсқалары. Бұрыштардағы L-профильді бекіту тораптары.',
        en: 'Options for forming internal corners from stud profiles. L-profile attachment nodes in corners.', ar: 'خيارات تشكيل الزوايا الداخلية من ملفات الحامل. عقد تثبيت الملف L في الزوايا.'
      },
      coverImage: `${API_URL}/api/album1/12`,
      slides: [
        {
          title: { ru: 'Узлы внутреннего угла', kz: 'Ішкі бұрыш тораптары', en: 'Internal Corner Nodes' },
          image: `${API_URL}/api/album1/11`,
          content: {
            ru: 'Формируется внутренний угол из стоечных профилей. От перпендикулярной плоскости угла отмеряется необходимая ширина, затем устанавливается следующий стоечный профиль. Подробный монтаж L-профиля описан далее.',
            kz: 'Тіреу профильдерінен ішкі бұрыш қалыптасады. Бұрыштың перпендикуляр жазықтығынан қажетті ені өлшенеді, содан кейін келесі тіреу профилі орнатылады.',
            en: 'Internal corner is formed from stud profiles. Required width is measured from the perpendicular plane of the corner, then the next stud profile is installed.', ar: 'تتشكل الزاوية الداخلية من ملفات الحامل. يُقاس العرض المطلوب من المستوى العمودي للزاوية، ثم يُركَّب ملف الحامل التالي.'
          }
        },
        {
          title: { ru: 'Узлы внутреннего угла', kz: 'Ішкі бұрыш тораптары', en: 'Internal Corner Nodes', ar: 'عقد الزوايا الداخلية' },
          image: `${API_URL}/api/album1/12`,
          content: {
            ru: 'Альтернативный вариант формирования внутреннего угла. Два стоечных профиля соединяются под прямым углом с использованием металлических скоб. Важно обеспечить жёсткость конструкции и правильный угол.',
            kz: 'Ішкі бұрышты қалыптастырудың балама нұсқасы. Екі тіреу профилі металл жақшалар арқылы тік бұрышпен қосылады. Конструкцияның қаттылығын және дұрыс бұрышты қамтамасыз ету маңызды.',
            en: 'Alternative option for internal corner formation. Two stud profiles are connected at a right angle using metal brackets. Ensuring structural rigidity and correct angle is important.', ar: 'خيار بديل لتشكيل الزوايا الداخلية. يتم توصيل ملفَّي حامل على زاوية قائمة باستخدام أقواس معدنية. من المهم ضمان صلابة الهيكل وصحة الزاوية.'
          }
        },
        {
          title: { ru: 'Завершение узла и проверка', kz: 'Торапты аяқтау және тексеру', en: 'Node Completion and Verification', ar: 'إتمام العقد والتحقق منها' },
          image: `${API_URL}/api/album1/13`,
          content: {
            ru: 'После сборки угловых узлов проверяется жёсткость, вертикальность и ровность каркаса. Все профили должны быть надёжно закреплены саморезами. Каркас готов к монтажу панелей.',
            kz: 'Бұрыш тораптарын жинағаннан кейін каркастың қаттылығы, тіктігі және тегістігі тексеріледі. Барлық профильдер бұрандалармен мықтап бекітілуі керек. Каркас панельдерді монтаждауға дайын.',
            en: 'After assembling corner nodes, check the frame rigidity, verticality, and flatness. All profiles must be securely fastened with screws. The frame is ready for panel installation.', ar: 'بعد تجميع العقد الزاوية، تحقق من صلابة الهيكل واستقامته الرأسية واستوائه. يجب أن تكون جميع الملفات مثبتة بإحكام بالبراغي. الهيكل جاهز لتركيب الألواح.'
          }
        }
      ]
    }
  ];

  const t = content[language] || content.en || content.ru;

  // Filter sections by category
  const filteredSections = activeCategory === 'all' 
    ? installationSections 
    : installationSections.filter(s => s.category === activeCategory);

  const openSection = (section) => {
    setSelectedSection(section);
    setCurrentSlide(0);
    document.body.style.overflow = 'hidden';
  };

  const closeSection = () => {
    setSelectedSection(null);
    setCurrentSlide(0);
    document.body.style.overflow = 'unset';
  };

  const nextSlide = () => {
    if (selectedSection && currentSlide < selectedSection.slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // PDF download links (proxied through /api/docs to hide CDN origin)
  const pdfUrl = `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-3.pdf`;
  const pdfUrl2 = `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-2.pdf`;
  const pdfUrl1 = `${API_URL}/api/docs/albom-tekhnicheskih-reshenii-1.pdf`;
  const instructionPdfUrl = `${API_URL}/api/docs/instrukciya-perevozka-hranenie-ekspluataciya.pdf`;

  // Category keys for filtering
  const categoryKeys = ['all', 'scheme', 'instruction', 'joints', 'corners', 'frame', 'partitions', 'prep', 'extra'];

  return (
    <div className="pt-20 bg-[#061d36]" data-testid="installation-page">
      <SEOHead 
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
      />
      {/* Slide Modal */}
      {selectedSection && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
          onClick={closeSection}
        >
          <div 
            className="relative w-full max-w-5xl bg-white rounded-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#061d36] p-4 text-white">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold">{selectedSection.title[language]}</h2>
                <button
                  onClick={closeSection}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-1 text-white/70 text-sm">
                {currentSlide + 1} {t.slideOf} {selectedSection.slides.length}
              </div>
            </div>
            
            {/* Slide Content with Image */}
            <div className="grid md:grid-cols-2 gap-0">
              {/* Image */}
              <div className="bg-gray-100 flex items-center justify-center p-4 min-h-[300px]">
                <LazyImage 
                  src={selectedSection.slides[currentSlide].image}
                  alt={selectedSection.slides[currentSlide].title[language]}
                  className="max-w-full max-h-[60vh] rounded-lg shadow-lg"
                />
              </div>
              
              {/* Text Content */}
              <div className="p-6 flex flex-col justify-center">
                <h3 className="text-2xl font-bold text-[#061d36] mb-4">
                  {selectedSection.slides[currentSlide].title[language]}
                </h3>
                <p className="text-[#061d36]/70 text-lg leading-relaxed">
                  {selectedSection.slides[currentSlide].content[language]}
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between p-4 border-t bg-gray-50">
              <button
                onClick={prevSlide}
                disabled={currentSlide === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentSlide === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[#061d36] hover:bg-gray-200'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
                {t.prev}
              </button>

              {/* Slide Indicators */}
              <div className="flex gap-2">
                {selectedSection.slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx)}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      idx === currentSlide ? 'bg-[#061d36]' : 'bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <button
                onClick={nextSlide}
                disabled={currentSlide === selectedSection.slides.length - 1}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  currentSlide === selectedSection.slides.length - 1
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[#061d36] hover:bg-gray-200'
                }`}
              >
                {t.next}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <section className="py-8 pb-16 bg-[#061d36] text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="installation-title">{t.header.title}</h1>
          <p className="text-lg text-blue-200 max-w-3xl mx-auto mb-8">{t.header.subtitle}</p>
          
          {/* Download PDF Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={instructionPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
              data-testid="download-instruction-btn"
            >
              <Download className="w-5 h-5" />
              {language === 'ru' ? 'Инструкция по эксплуатации' : 
               language === 'kz' ? 'Пайдалану нұсқаулығы' : 
               'Operation Manual'}
            </a>
            <a
              href={pdfUrl1}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
              data-testid="download-pdf1-btn"
            >
              <Download className="w-5 h-5" />
              {language === 'ru' ? 'Скачать Альбом №1 (АТР)' : 
               language === 'kz' ? 'Альбом №1 жүктеу (АТР)' : 
               'Download Album №1 (ATR)'}
            </a>
            <a
              href={pdfUrl2}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
              data-testid="download-pdf2-btn"
            >
              <Download className="w-5 h-5" />
              {language === 'ru' ? 'Скачать Альбом №2 (АТР)' : 
               language === 'kz' ? 'Альбом №2 жүктеу (АТР)' : 
               'Download Album №2 (ATR)'}
            </a>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-semibold transition-colors"
              data-testid="download-pdf-btn"
            >
              <Download className="w-5 h-5" />
              {language === 'ru' ? 'Скачать Альбом №3 (АТР)' : 
               language === 'kz' ? 'Альбом №3 жүктеу (АТР)' : 
               'Download Album №3 (ATR)'}
            </a>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#F5F5F0] border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {categoryKeys.map((key) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                data-testid={`category-filter-${key}`}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === key
                    ? 'bg-[#061d36] text-white shadow-md'
                    : 'bg-gray-100 text-[#061d36]/80 hover:bg-gray-200'
                }`}
              >
                {t.categories[key]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Installation Sections Grid */}
      <section className="py-8 bg-[#F5F5F0]">
        <div className="container mx-auto px-4">
          {filteredSections.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {filteredSections.map((section) => (
                <div
                  key={section.id}
                  data-testid={`section-card-${section.id}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all cursor-pointer group"
                  onClick={() => openSection(section)}
                >
                  {/* Cover Image */}
                  <div className="relative h-48 overflow-hidden bg-gray-200">
                    <LazyImage 
                      src={section.coverImage}
                      alt={section.title[language]}
                      className="w-full h-full"
                    />
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="text-lg font-bold text-[#061d36] mb-2 group-hover:text-blue-600 transition-colors">
                      {section.title[language]}
                    </h3>
                    <p className="text-[#061d36]/70 text-sm line-clamp-2 mb-4">
                      {section.description[language]}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-blue-600 text-sm font-medium">
                        <FileText className="w-4 h-4 mr-1" />
                        {section.slides.length} {language === 'ru' ? 'слайдов' : language === 'kz' ? 'слайд' : 'slides'}
                      </div>
                      {/* Category Badge */}
                      <div className="bg-[#061d36] text-white text-xs font-medium px-3 py-1 rounded">
                        {t.categories[section.category]}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">
                {language === 'ru' ? 'В этой категории пока нет материалов' : 
                 language === 'kz' ? 'Бұл санатта әлі материалдар жоқ' : 
                 'No materials in this category yet'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="pt-16 py-8 pb-16 bg-[#061d36]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            {language === 'ru' ? 'Нужна помощь с монтажом?' : 
             language === 'kz' ? 'Монтажға көмек қажет пе?' : 
             'Need help with installation?'}
          </h2>
          <p className="text-blue-200 mb-8 max-w-2xl mx-auto">
            {language === 'ru' ? 'Наши специалисты готовы помочь вам с техническими вопросами и предоставить бесплатный шеф-монтаж' : 
             language === 'kz' ? 'Біздің мамандар техникалық сұрақтар бойынша көмектесуге және тегін шеф-монтаж қызметін ұсынуға дайын' : 
             'Our specialists are ready to help you with technical questions and provide free installation supervision'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#8B4444] hover:bg-[#7A3838] text-white rounded-xl font-bold transition-colors"
          >
            {language === 'ru' ? 'Связаться с нами' : 
             language === 'kz' ? 'Бізбен байланысу' : 
             'Contact us'}
          </a>
        </div>
      </section>
    </div>
  );
};

export default InstallationPage;
